'use client'

import { useEffect, useCallback, useState, type ReactNode, type ReactPortal } from 'react'
import { createPortal } from 'react-dom'

export interface ModalProps {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg'
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
}: ModalProps): ReactPortal | null {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose]
  )

  useEffect(() => {
    if (open && typeof window !== 'undefined') {
      window.document.addEventListener('keydown', handleKeyDown)
      window.document.body.style.overflow = 'hidden'
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.document.removeEventListener('keydown', handleKeyDown)
        window.document.body.style.overflow = ''
      }
    }
  }, [open, handleKeyDown])

  if (!open || !mounted) return null

  const sizeStyles: Record<string, string> = {
    sm: '400px',
    md: '500px',
    lg: '700px',
  }

  const modalContent = (
    <div
      className="fui-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title !== undefined ? 'modal-title' : undefined}
    >
      <div className="fui-modal" style={{ maxWidth: sizeStyles[size] }}>
        {title !== undefined && (
          <div className="fui-modal-header">
            <h2 id="modal-title" className="fui-modal-title">
              {title}
            </h2>
            <button
              type="button"
              className="fui-modal-close"
              onClick={onClose}
              aria-label="Close modal"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M15 5L5 15M5 5l10 10" />
              </svg>
            </button>
          </div>
        )}
        <div className="fui-modal-body">{children}</div>
        {footer !== undefined && <div className="fui-modal-footer">{footer}</div>}
      </div>
    </div>
  )

  // Only use portal in browser environment
  if (typeof window === 'undefined') {
    return null
  }

  return createPortal(modalContent, window.document.body)
}
